// noinspection JSCommentMatchesSignature

'use strict';

import * as Sentry from '@sentry/browser';

/**
 * Sentry configuration object.
 *
 * @typedef  {Object}                   SentryConfig
 * @property {string}                   dsn
 * @property {('dev'|'prod'|'staging')} [environment]
 * @property {Boolean}                  [debug]
 * @property {(Array|string)}           [thirdPartyFilterKeys]
 * @property {number}                   [replaysSessionSampleRate]
 * @property {number}                   [replaysOnErrorSampleRate]
 * @property {Object}                   [replayOptions]
 * @property {Array}                    [ignoredErrors] JS errors to ignore (regex)
 * @property {string}                   [errorFilterBehavior] See https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-thirdpartyerrorfilterintegration
 */

/**
 * @param {(string|SentryConfig)} [dsn] Either the dsn string or a {@link SentryConfig} object.
 */
function initSentry(
    dsn,
    environment              = 'dev',
    debug                    = false,
    thirdPartyFilterKeys     = null,
    replaysSessionSampleRate = 0.0,
    replaysOnErrorSampleRate = 0.0,
    replayOptions            = {
        maskAllText:   false,
        blockAllMedia: false,

        networkDetailAllowUrls: [
            /^.+/,
        ],

        networkDetailDenyUrls: [
            /^https?:\/\/.*\.google\.com/,
            /^https?:\/\/.*\.doubleclick\.net/,
            /^https?:\/\/.*\.googlesyndication\.com/,
            /^https?:\/\/matomo\.fify\.services/,
        ],
    },
    ignoredErrors = [],
    errorFilterBehavior = 'drop-error-if-contains-third-party-frames'
) {
    if (Sentry.isInitialized()) {
        if (debug) {
            console.debug('Sentry already initialized')
        }

        return;
    }

    if (dsn && typeof dsn === 'object') {
        let config               = dsn;
        dsn                      = config.dsn || null;
        environment              = config.environment || environment;
        debug                    = config.debug || debug;
        thirdPartyFilterKeys     = config.thirdPartyFilterKeys || thirdPartyFilterKeys;
        replaysSessionSampleRate = config.replaysSessionSampleRate || replaysSessionSampleRate;
        replaysOnErrorSampleRate = config.replaysOnErrorSampleRate || replaysOnErrorSampleRate;
        replayOptions            = config.replayOptions || replayOptions;
        ignoredErrors            = config.ignoredErrors || ignoredErrors;
        errorFilterBehavior      = config.errorFilterBehavior || errorFilterBehavior;
    }

    const enabled = !!dsn;

    const integrations = [
        Sentry.replayIntegration(replayOptions),
    ];

    if (thirdPartyFilterKeys) {
        const filterKeys = typeof thirdPartyFilterKeys !== 'object' ? thirdPartyFilterKeys : [thirdPartyFilterKeys];

        integrations.push(
            Sentry.thirdPartyErrorFilterIntegration({
                filterKeys: filterKeys,
                behaviour:  errorFilterBehavior,
            })
        );
    }

    const config = {
        enabled:                  enabled,
        debug:                    debug,
        dsn:                      dsn,
        environment:              environment,
        integrations:             integrations,
        replaysSessionSampleRate: replaysSessionSampleRate,
        replaysOnErrorSampleRate: replaysOnErrorSampleRate,

        ignoreErrors: [
            // mostly on deploy
            /^ChunkLoadError: Loading chunk/,
            /^ChunkLoadError: Loading CSS chunk/,

            // uncaught failed/aborted fetch in multiple browsers and failed requests
            /^TypeError: Failed to fetch/,
            /^TypeError: Load failed/,
            /^TypeError: NetworkError when attempting to fetch resource/,
            /^AbortError: The user aborted a request/,
            /^AbortError: The operation was aborted/,
            /^AbortError: Fetch is aborted/,
            /^AbortError: cancelled/,
            /^AbortError: Cancelled/,
            /^TypeError: Content-Length header of network response exceeds response Body./,

            // unhandled promises
            /^UnhandledRejection: Non-Error promise rejection captured with value:/,

            // resize observer
            /^ResizeObserver loop limit exceeded/,
            /^ResizeObserver loop completed with undelivered notifications/,

            // View transition errors (mostly mobile)
            /^InvalidStateError: Skipping view transition/,
            /^InvalidStateError: View transition was skipped/,
            /^InvalidStateError: One of the transitioned elements has become hidden/,
            /^AbortError: Old view transition aborted by new view transition/,
            /^TimeoutError: View transition update callback timed out/,
            /^UnhandledRejection: Object captured as promise rejection with keys: isFromCancelledTransition/,

            // Safari old doesn't support intersection observer
            /^ReferenceError: Can't find variable: IntersectionObserver/,

            // Safari, probably an extension problem
            /^Error: Invalid call to runtime.sendMessage/,

            // probably in-app browser of some Android apps
            /^TypeError: Cannot read properties of undefined \(reading 'domInteractive'\)/,

            // hard to reproduce problem, hard to fix, see https://github.com/symfony/ux/issues/2623
            /^Error: Tom Select already initialized on this element/,
            /^TypeError: undefined is not an object \(evaluating 'this.tomSelect.destroy'\)/,
            /^TypeError: Cannot read properties of undefined \(reading 'destroy'\)/,
            /^TypeError: this.tomSelect is undefined/,

            // probably sandboxed bots and/or old browsers, also Sentry JS errors
            /^SecurityError: Failed to read a named property/,
            /^TypeError: Cannot read properties of null \(reading 'touches'\)/,
            /^Error: db/,
            /^Error: missing id/,
            /^Error: Method not found/,
            /^Error: Multiple Sentry Session Replay instances are not supported/,

            // Alpine (if something failed before?)
            /^RangeError: Maximum call stack size exceeded/,

            // Turbo
            /^TypeError: undefined is not an object \(evaluating 'this.delegate.connect'\)/,
            /^TypeError: undefined is not an object \(evaluating 'this.delegate.disconnect'\)/,
            /^Error: The response \(200\) did not contain the expected <turbo-frame/,

            // Live Component requests failing
            /^Error: Component not found for element/,
        ].concat(ignoredErrors),
    };

    if (debug) {
        console.debug('Initializing sentry', config);
    }

    Sentry.init(config);
}


window.__sentry      = window.__sentry || {};
window.__sentry.init = initSentry;

if (typeof window.__sentry.config === 'object' && window.__sentry.config) {
    initSentry(window.__sentry.config);
}
